/**
 * client/src/components/ui/collapsible-section.tsx
 *
 * The shared collapse/expand page section: a full-width header button (visible
 * chevron affordance — DESIGN-SYSTEM §7.1 rule 4, 44px target §3.1) that toggles
 * its body, with the open state persisted via the shared `usePersistedBoolean`
 * (same posture as the desktop sidebar, §2.2). When collapsed, an optional
 * one-line `summary` renders in the header so the section still answers at a
 * glance ("Deal account · Westpac · Verified") without expanding.
 *
 * Accessibility: the header is a real <button> inside the heading (the WAI-ARIA
 * disclosure pattern) carrying `aria-expanded` + `aria-controls`; the collapsed
 * body is `visibility: hidden` (out of the tab order and the accessibility tree)
 * but stays MOUNTED so child state (filters, form fields) survives a collapse.
 * Children may be a render function receiving `open`, so a child can gate its
 * own data fetching on visibility (e.g. `enabled={open}` on a query panel).
 * Motion: 220ms grid-rows transition (≤350ms cap), disabled under
 * `prefers-reduced-motion` via `motion-reduce:transition-none`.
 *
 * Optional `count`: a numeric badge folded into the header's accessible name
 * after the title ("Communications · 12"), for sections whose header should
 * answer "how many?" at a glance (connection-view redesign, #1337 AC-13). Part
 * of the accessible name, not a separate visual chip — screen readers announce
 * "Communications · 12" as the button label.
 */

import { type ReactNode } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/cn";
import { usePersistedBoolean } from "@/lib/use-persisted-boolean";

const STORAGE_PREFIX = "collapsible-section:";

/**
 * The persistence key this component's own internal `usePersistedBoolean`
 * uses for a given `id` — exported so a CONTROLLED caller that owns its own
 * `usePersistedBoolean` per widget (e.g. `dashboard-settings.tsx`) can key
 * into the exact same storage slot an uncontrolled instance would use,
 * without re-typing the `"collapsible-section:"` prefix as a second literal
 * (simplify pass, #2244 — `tests/unit/collapsible-section-backcompat.test.tsx`
 * pins the exact string this produces).
 */
export function collapsibleSectionStorageKey(id: string): string {
  return STORAGE_PREFIX + id;
}

export interface CollapsibleSectionProps {
  /** Stable id — names the persistence key and the aria-controls target. */
  id: string;
  title: string;
  /**
   * Optional numeric badge folded into the header's accessible name after the
   * title ("Communications · 12"). Additive — omitted when undefined.
   */
  count?: number;
  /** Optional leading icon (rendered aria-hidden; the title carries the name). */
  icon?: ReactNode;
  /**
   * Extra classes for the header button. The default header is flush to its
   * container's edges, which is right for callers that already pad their own
   * wrapper — a caller that draws its own card border must pass its own
   * horizontal padding (e.g. `px-4`) so the title isn't pressed against the
   * border. Additive only; never overrides layout/focus classes.
   */
  headerClassName?: string;
  /** One-line peek shown in the header. Rich content is allowed. */
  summary?: ReactNode;
  /**
   * When the summary is shown. `"collapsed"` (default) preserves the original
   * behaviour for every existing caller. `"always"` keeps it rendered while
   * open — used by the connection card, where closing a section must lose
   * detail but never signal.
   */
  summaryVisibility?: "collapsed" | "always";
  /** Initial state when nothing is persisted yet. Defaults to open. */
  defaultOpen?: boolean;
  /** Semantic heading depth when the section is nested. Defaults to 2. */
  headingLevel?: 2 | 3 | 4;
  /**
   * Controlled open state. Omit BOTH `open` and `onOpenChange` to keep
   * today's self-managed, persisted behaviour (internal `usePersistedBoolean`
   * state, same storage key, same `defaultOpen` fallback). Supplying BOTH
   * hands control to the caller: `open` decides what renders, and every
   * toggle interaction calls `onOpenChange(next)` instead of touching
   * internal/persisted state. Supplying only one of the pair is not a
   * supported combination — the component still treats itself as
   * uncontrolled (it only defers to `open` when a change handler exists to
   * report user interactions back through), so a caller that means to drive
   * this component must supply both.
   */
  open?: boolean;
  onOpenChange?: (next: boolean) => void;
  /**
   * Renders `{ level, peek? }` as siblings of the toggle button, below the
   * label row (security-overview plan, AC-4); receives `open` so a caller can
   * supply `peek` only while collapsed. Additive — omitted, the header is
   * byte-identical to before (`summary`/`summaryVisibility`).
   */
  headerLines?: (open: boolean) => { level: ReactNode; peek?: ReactNode };
  /** A control rendered as a TRUE SIBLING of the toggle button, never nested
   *  inside it (AC-1, e.g. a level-definition disclosure); clicks inside it
   *  never toggle. Rendered only with `headerLines`; additive. */
  headerAside?: ReactNode;
  /** Section body, or a render function receiving the live `open` state. */
  children: ReactNode | ((open: boolean) => ReactNode);
}

interface MultiLineHeaderProps {
  title: string;
  headingLevel: 2 | 3 | 4;
  open: boolean;
  bodyId: string;
  onToggle: () => void;
  lines: { level: ReactNode; peek?: ReactNode };
  aside?: ReactNode;
}

/**
 * The multi-line header variant (AC-4): the real <button> wraps ONLY the label
 * text (keeping `aria-expanded`/`aria-controls`, the disclosure pattern), while
 * the level/peek lines and the optional `aside` render as its SIBLINGS — no
 * interactive element nests inside another (AC-1). The wrapper forwards a click
 * anywhere in the header to the toggle, except one inside `aside`. */
function MultiLineHeader(props: MultiLineHeaderProps): JSX.Element {
  const { title, headingLevel, open, bodyId, onToggle, lines, aside } = props;
  return (
    <div
      className="flex flex-col gap-1.5 py-2"
      onClick={(event) => {
        if ((event.target as HTMLElement).closest("[data-header-aside]")) return;
        onToggle();
      }}
    >
      {/* The button carries no onClick of its own: the click bubbles to the
          wrapper, which owns the single toggle (a second handler here would flip
          twice). Enter/Space fires a click, so it stays keyboard-operable. */}
      <div className="flex items-center gap-2">
        <SectionHeading level={headingLevel}>
          <button
            type="button"
            aria-expanded={open}
            aria-controls={bodyId}
            className="flex min-h-[44px] items-center text-left rounded-[var(--radius-md)] text-[length:var(--text-caption-size)] font-semibold uppercase tracking-wide text-text-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-strenn-emerald/50"
          >
            {title}
          </button>
        </SectionHeading>
        {aside && <span data-header-aside="true">{aside}</span>}
        <ChevronDown
          size={16}
          aria-hidden="true"
          className={cn(
            "ml-auto flex-none text-text-tertiary transition-transform duration-[220ms] motion-reduce:transition-none",
            open && "rotate-180",
          )}
        />
      </div>
      {lines.level}
      {lines.peek}
    </div>
  );
}

interface SectionHeadingProps {
  level: 2 | 3 | 4;
  children: ReactNode;
}

function SectionHeading({ level, children }: SectionHeadingProps): JSX.Element {
  if (level === 3) return <h3 className="m-0">{children}</h3>;
  if (level === 4) return <h4 className="m-0">{children}</h4>;
  return <h2 className="m-0">{children}</h2>;
}

/** A page section the user can collapse/expand, with the state persisted. */
export function CollapsibleSection({
  id,
  title,
  count,
  icon,
  headerClassName,
  summary,
  summaryVisibility = "collapsed",
  defaultOpen = true,
  headingLevel = 2,
  open: controlledOpen,
  onOpenChange,
  headerLines,
  headerAside,
  children,
}: CollapsibleSectionProps): JSX.Element {
  const [persistedOpen, togglePersistedOpen] = usePersistedBoolean(
    STORAGE_PREFIX + id,
    defaultOpen,
  );
  // Controlled only when BOTH props are supplied — see the `open` doc comment
  // for why one-without-the-other still falls back to uncontrolled.
  const isControlled = controlledOpen !== undefined && onOpenChange !== undefined;
  const open = isControlled ? controlledOpen : persistedOpen;
  const handleToggle = (): void => {
    if (isControlled) {
      onOpenChange(!open);
    } else {
      // Route through the hook's own `toggle` (functional `setValue(prev =>
      // !prev)` internally) rather than computing the next value from this
      // render's `open` closure — two toggles firing before a re-render
      // lands must still net out to two real flips, not one dropped update.
      togglePersistedOpen();
    }
  };
  const bodyId = `collapsible-section-${id}`;
  const accessibleName = count === undefined ? title : `${title} · ${count}`;

  return (
    <section aria-label={accessibleName} data-collapsible-section={id}>
      {headerLines ? (
        <MultiLineHeader
          title={title}
          headingLevel={headingLevel}
          open={open}
          bodyId={bodyId}
          onToggle={handleToggle}
          lines={headerLines(open)}
          aside={headerAside}
        />
      ) : (
      <SectionHeading level={headingLevel}>
        <button
          type="button"
          onClick={handleToggle}
          aria-expanded={open}
          aria-controls={bodyId}
          className={cn(
            "flex w-full items-center gap-2 min-h-[44px] text-left rounded-[var(--radius-md)] focus:outline-none focus-visible:ring-2 focus-visible:ring-strenn-emerald/50",
            headerClassName,
          )}
        >
          {icon && (
            <span className="flex-none text-text-secondary" aria-hidden="true">
              {icon}
            </span>
          )}
          <span data-role="section-title" className="text-[length:var(--text-subheading-size)] font-semibold text-text-primary">
            {accessibleName}
          </span>
          {summary && (summaryVisibility === "always" || !open) && (
            <>
              {/* A separate sibling span (not folded into the summary span
                  itself, so an exact-text `getByText(summary)` lookup still
                  resolves) — a bare adjacent <span> with no separator fuses
                  into the title in the computed accessible name
                  ("VerificationIdentity checks"). NOT aria-hidden: it must
                  stay part of the accessible name (mirrors the same " · "
                  the `count` badge above already uses) so the announced
                  name reads as two clauses, not a run-on. */}
              <span className="text-text-tertiary flex-none">{" · "}</span>
              {/* J3 (final-fix-brief.md, MAJOR) — AC-11 bars `--text-tertiary`
                  from structural labels, reserving it for genuinely
                  de-emphasised content (em-dash zeros, empty-state text).
                  The closed header's summary is the section's ANSWER when
                  collapsed (AC-2's whole premise), so it belongs at
                  `--text-secondary`, matching the accepted fixture
                  (connection-widget-simple.html). `data-role="section-summary"`
                  lets the shared a11y contrast guard
                  (connection-card-a11y.test.tsx) cover it — it previously had
                  no `data-role` at all, so a summary at ANY colour passed. */}
              <span
                data-role="section-summary"
                className="min-w-0 truncate text-[length:var(--text-caption-size)] font-normal text-text-secondary"
              >
                {summary}
              </span>
            </>
          )}
          <ChevronDown
            size={16}
            aria-hidden="true"
            className={cn(
              "ml-auto flex-none text-text-tertiary transition-transform duration-[220ms] motion-reduce:transition-none",
              open && "rotate-180",
            )}
          />
        </button>
      </SectionHeading>
      )}
      <div
        id={bodyId}
        className={cn(
          "grid transition-[grid-template-rows,visibility] duration-[220ms] motion-reduce:transition-none",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr] invisible",
        )}
      >
        {/* Stays mounted while hidden so child state (filters, inputs) survives. */}
        <div className="overflow-hidden">
          {typeof children === "function" ? children(open) : children}
        </div>
      </div>
    </section>
  );
}
